import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fmtMYTDate } from "@/lib/myt";
import { Gift, LineChart, Network, Share2, ShieldAlert, UserCheck, UserPlus, Users } from "lucide-react";
import api from "@/lib/api";

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((res) => setData(res.data)).catch(() => {});
  }, []);

  const cards = [
    { label: "Total Members", value: data?.total_members, icon: Users, testId: "admin-total-members-card", color: "text-white", to: "/admin/members" },
    { label: "Active Members", value: data?.active_members, icon: UserCheck, testId: "admin-active-members-card", color: "text-emerald-400", to: "/admin/members" },
    { label: "New Registrations (7d)", value: data?.new_members_this_week, icon: UserPlus, testId: "admin-new-members-card", color: "text-emerald-400" },
    { label: "Suspended Accounts", value: data?.suspended_accounts, icon: ShieldAlert, testId: "admin-suspended-card", color: "text-red-400" },
    { label: "Introducing Brokers", value: data?.total_ibs, icon: Network, testId: "admin-total-ibs-card", color: "text-[#D4AF37]", to: "/admin/ibs" },
    { label: "Referred Members", value: data?.total_referred, icon: Share2, testId: "admin-referred-card", color: "text-white", to: "/admin/members" },
    { label: "Indicator Accesses", value: data?.indicator_accesses, icon: LineChart, testId: "admin-indicator-accesses-card", color: "text-white", to: "/admin/indicators" },
    { label: "Pending Redemptions", value: data?.pending_redemptions, icon: Gift, testId: "admin-pending-redemptions-card", color: "text-[#D4AF37]", to: "/admin/rewards" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <p className="eyebrow-label mb-2">Control Center</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="admin-dashboard-heading">
          Ecosystem Overview
        </h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-overview-cards">
        {cards.map((card) => {
          const inner = (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-[#64748B] tracking-wide">{card.label}</p>
                <card.icon className="h-4 w-4 text-[#D4AF37]" />
              </div>
              <p className={`font-mono text-3xl font-semibold ${card.color}`}>
                {card.value ?? "—"}
              </p>
            </>
          );
          return card.to ? (
            <Link key={card.label} to={card.to} className="card-lux card-lux-hover p-6 block" data-testid={card.testId}>
              {inner}
            </Link>
          ) : (
            <div key={card.label} className="card-lux p-6" data-testid={card.testId}>
              {inner}
            </div>
          );
        })}
      </div>

      <div className="card-lux p-8">
        <p className="eyebrow-label mb-6">Recent Registrations</p>
        <div className="space-y-1" data-testid="admin-recent-members">
          {!data ? (
            <p className="text-sm text-[#64748B]">Loading...</p>
          ) : data.recent_members.length === 0 ? (
            <p className="text-sm text-[#64748B]">
              No members registered yet. Create an IB first, then members can register with the IB's referral code.
            </p>
          ) : (
            data.recent_members.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-3 border-b border-[#222530]/60 last:border-0">
                <div>
                  <p className="text-sm font-medium text-white">{c.full_name}</p>
                  <p className="text-xs text-[#64748B] font-mono">{c.username}</p>
                </div>
                <p className="text-xs text-[#64748B]">
                  {c.created_at ? fmtMYTDate(c.created_at) : "—"}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
